"use client";

import {
  Cell,
  Column,
  ColumnProps,
  Row,
  RowProps,
  TableBody,
  TableBodyProps,
} from "@react-stately/table";
import { useRef } from "react";
import { AriaTableProps, useTable } from "react-aria";
import { TableStateProps, useTableState } from "react-stately";

import { csx } from "../utils";
import {
  TableCell,
  TableColumnHeader,
  TableHeaderRow,
  TableRow,
  TableRowGroup,
} from "./TableComponents";

import "./Table.css";

export { Section, TableHeader } from "@react-stately/table";
export {
  ProtonCell as Cell,
  ProtonColumn as Column,
  ProtonRow as Row,
  ProtonTableBody as TableBody,
};

// Override TS for Column to support proton specific props.
export interface ProtonColumnProps<T> extends ColumnProps<T> {
  align?: "left" | "center" | "right";
  onClick?: (key: string) => void;
}

// Override TS for Row to support proton specific props.
export interface ProtonRowProps<T = object> extends RowProps<T> {
  backgroundColor?: string;
  onClick?: (key: string) => void;
}

// Override TS for TableBody to support proton specific props.
export interface ProtonTableBodyProps<T = object> extends TableBodyProps<T> {
  showLines?: boolean;
}

const ProtonColumn = Column as <T>(props: ProtonColumnProps<T>) => JSX.Element;
const ProtonCell = Cell as <T>(props: ProtonColumnProps<T>) => JSX.Element;
const ProtonRow = Row as <T = object>(props: ProtonRowProps<T>) => JSX.Element;
const ProtonTableBody = TableBody as <T = object>(
  props: ProtonTableBodyProps<T>
) => JSX.Element;

interface TableProps extends AriaTableProps, TableStateProps<object> {
  headerProps?: {
    isHidden?: boolean;
  };
}

export function Table(props: TableProps) {
  let state = useTableState({
    ...props,
  });

  let ref = useRef();
  let { collection } = state;
  let { gridProps } = useTable(
    { ...props, "aria-label": props["aria-label"] || "Table" },
    state,
    ref
  );

  return (
    <table {...gridProps} ref={ref} className={csx("proton-Table")}>
      <TableRowGroup type="thead" isHidden={props.headerProps?.isHidden}>
        {collection.headerRows.map((headerRow) => (
          <TableHeaderRow key={headerRow.key} item={headerRow} state={state}>
            {Array.from(collection.getChildren(headerRow.key)).map((column) => (
              <TableColumnHeader
                key={column.key}
                column={column}
                state={state}
              />
            ))}
          </TableHeaderRow>
        ))}
      </TableRowGroup>

      <TableRowGroup type="tbody">
        {Array.from(collection.body.childNodes).map((row) => (
          <TableRow
            key={row.key}
            item={row}
            state={state}
            backgroundColor={row.props.backgroundColor}
            showLines={collection.body.props.showLines}
            onClick={row.props.onClick}
          >
            {Array.from(collection.getChildren(row.key)).map((cell) => (
              <TableCell
                key={cell.key}
                cell={cell}
                state={state}
                width={cell.props.width}
                onClick={cell.props.onClick}
              />
            ))}
          </TableRow>
        ))}
      </TableRowGroup>
    </table>
  );
}
